/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Editor Live Stats & Save Status Component (Task 10.2.1 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useEditorStore } from '../../stores/useEditorStore';
import { useI18n } from '../../stores/useI18nStore';
import { CheckCircle2, Clock, RefreshCw, AlertCircle } from 'lucide-react';

export const EditorStats: React.FC = () => {
  const { stats, status, lastSavedAt, isWordCountVisible } = useEditorStore();
  const { t } = useI18n();

  if (!isWordCountVisible) {
    return null;
  }

  const formatLastSaved = (timestamp: number | null) => {
    if (!timestamp) return t('editor.notSaved');
    const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
    if (diffSeconds < 5) return t('editor.justNow');
    if (diffSeconds < 60) return t('editor.secondsAgo', { seconds: diffSeconds });
    return t('editor.minutesAgo', { minutes: Math.floor(diffSeconds / 60) });
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 rounded-xl border border-surface-border bg-surface text-xs text-foreground-muted">
      {/* 1. Document Metrics */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-foreground">{stats.words.toLocaleString()}</span>
          <span>{t('editor.words')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-foreground">{stats.characters.toLocaleString()}</span>
          <span>{t('editor.chars')}</span>
        </div>
        <div className="flex items-center gap-1.5 border-l border-surface-border pl-4">
          <Clock className="w-3.5 h-3.5" />
          <span>{t('editor.readTime', { minutes: stats.readingTimeMinutes })}</span>
        </div>
      </div>

      {/* 2. Auto-save Status Indicator */}
      <div className="flex items-center gap-2">
        {status === 'saving' && (
          <div className="flex items-center gap-1.5 text-primary">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            <span>{t('editor.savingDraft')}</span>
          </div>
        )}

        {status === 'saved' && (
          <div className="flex items-center gap-1.5 text-emerald-500">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{t('editor.savedAt', { time: formatLastSaved(lastSavedAt) })}</span>
          </div>
        )}

        {status === 'unsaved' && (
          <div className="flex items-center gap-1.5 text-amber-500">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span>{t('editor.unsavedChanges')}</span>
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center gap-1.5 text-rose-500">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{t('editor.saveError')}</span>
          </div>
        )}
      </div>
    </div>
  );
};
